import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, TextField, Button, FormControl, InputLabel, Select, MenuItem, Grid, Divider,
  Card, CardContent, CircularProgress, Alert, FormHelperText, useTheme, styled
} from '@mui/material';
import { ArrowBack, Save, Cancel, Lock, LockOpen } from '@mui/icons-material';
import useFetchObject from '../../api/useFetchObject';
import useFetchObjects from '../../api/useFetchObjects';
import useUpdate from '../../api/useUpdate';
import { useAuth } from '../../context/AuthContext';
import { useDateFormat, DATE_FORMATS } from '../../context/DateFormatContext';
import { formatShamsiDate, formatGregorianDate, formatHijriDate } from '../../utils/dateUtils';
import LocalizedDatePicker from '../../components/common/LocalizedDatePicker';
import { RTLTextField, RTLFormControl, RTLButton } from '../../styles/rtlComponents';
import useRTL from '../../hooks/useRTL';

// Using centralized RTL components from styles/rtlComponents.js

const DocumentEdit = () => {
  const { t, i18n } = useTranslation(['documents', 'common']);
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const theme = useTheme();
  const { isRTL, getRTLProps } = useRTL();
  const { dateFormat } = useDateFormat();
  const token = user?.token;

  // Fetch latest assignment for this document to determine editability
  const { data: docAssignments } = useFetchObjects(
    'task-assignments/by-document-ids',
    { document_ids: id },
    token,
    { enabled: Boolean(id && token) }
  );

  // Determine if editing should be locked based on assignment status
  const latestAssignment = Array.isArray(docAssignments)
    ? docAssignments.find(a => a.document_id === id)
    : null;
  const lockedAssignmentStatuses = new Set(['IN_PROGRESS', 'REJECTED', 'FORWARDED', 'COMPLETED']);
  const isLocked = latestAssignment ? lockedAssignmentStatuses.has(latestAssignment.status) : false;

  // Format date based on selected date format
  const formatDate = (dateString, forInput = false) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    
    if (forInput) {
      return date.toISOString().split('T')[0];
    }
    
    return dateFormat === DATE_FORMATS.SHAMSI ? formatShamsiDate(date, isRTL) :
      dateFormat === DATE_FORMATS.QAMARI ? formatHijriDate(date, isRTL) :
      formatGregorianDate(date, isRTL);
  };

  // Fetch document data
  const { 
    data: document, 
    isLoading, 
    isError, 
    error 
  } = useFetchObject('document', 'documents', id, token);

  // Fetch related data for dropdowns
  const fetchOptions = { params: {}, token };
  const { data: departments = [] } = useFetchObjects('departments', fetchOptions);
  const { data: directorates = [] } = useFetchObjects('directorates', fetchOptions);
  const { data: deputyMinistries = [] } = useFetchObjects('deputy-ministries', fetchOptions);

  // Form state
  const initialFormState = {
    title: '', 
    reference_number: '', 
    document_date: '', 
    description: '', 
    type: '', 
    priority: '',
    status: '', 
    department_id: '', 
    directorate_id: '', 
    deputy_ministry_id: '', 
    source_type: 'INTERNAL',
    external_source_name: '', 
    due_date: '', 
    is_confidential: false, 
    number_of_pages: '',
    archive_registration_date: '', 
    archive_number: ''
  };
  
  const [formData, setFormData] = useState(initialFormState);
  const [formErrors, setFormErrors] = useState({});
  const [submitError, setSubmitError] = useState('');

  // Constants for dropdown options
  const documentTypes = ['مکتوب', 'استعلام', 'پیشنهاد', 'گزارش'];
  const priorities = ['عاجل', 'اطمینانیه', 'ابلاغیه', 'محرم', 'عادي'];
  const statuses = [
    { value: 'DRAFT', label: t('documents:status.draft') },
    { value: 'SUBMITTED', label: t('documents:status.submitted') },
    { value: 'IN_REVIEW', label: t('documents:status.in_review') },
    { value: 'APPROVED', label: t('documents:status.approved') },
    { value: 'REJECTED', label: t('documents:status.rejected') },
    { value: 'COMPLETED', label: t('documents:status.completed') },
    { value: 'CANCELLED', label: t('documents:status.cancelled') }
  ];
  const sourceTypes = [
    { value: 'INTERNAL', label: t('documents:source.internal') },
    { value: 'EXTERNAL', label: t('documents:source.external') },
    { value: 'PROVINCIAL', label: t('documents:source.provincial') }
  ];

  // Initialize form with fetched data
  useEffect(() => {
    if (document) {
      const formatDate = (dateStr) => dateStr ? dateStr.split('T')[0] : '';
      setFormData({
        ...initialFormState,
        ...document,
        document_date: formatDate(document.document_date),
        due_date: formatDate(document.due_date),
        archive_registration_date: formatDate(document.archive_registration_date)
      });
    }
  }, [document]);

  // Filter dependent dropdowns
  const filteredDirectorates = directorates.filter(dir => 
    !formData.deputy_ministry_id || dir.deputy_ministry_id === formData.deputy_ministry_id
  );
  const filteredDepartments = departments.filter(dept => 
    !formData.directorate_id || dept.directorate_id === formData.directorate_id
  );

  // Handlers
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (formErrors[name]) setFormErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validate = () => {
    const errors = {};
    if (!formData.title) errors.title = t('validation.required');
    if (!formData.reference_number) errors.reference_number = t('validation.required');
    if (!formData.type) errors.type = t('validation.required');
    if (!formData.priority) errors.priority = t('validation.required');
    if (!formData.status) errors.status = t('validation.required');
    if (formData.source_type === 'EXTERNAL' && !formData.external_source_name) {
      errors.external_source_name = t('validation.required_for_external');
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const { handleUpdate, loading: updateLoading } = useUpdate('documents', token);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isLocked) {
      const localizedStatus = latestAssignment?.status 
        ? t(`documents:assignment_status.${latestAssignment.status}`, latestAssignment.status)
        : 'N/A';
      
      setSubmitError(
        t('documents:edit_locked_message', {
          defaultValue:
            'Editing is disabled while this document is being processed (current assignment status: {{status}}). If changes are required, please contact the assigner.',
          status: localizedStatus,
        })
      );
      return;
    }
    if (!validate()) return setSubmitError(t('validation.fix_errors'));

    try {
      await handleUpdate(id, formData);
      navigate('/documents');
    } catch (err) {
      setSubmitError(err.message || t('common:error_occurred'));
    }
  };

  if (isLoading) return (
    <Box sx={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      minHeight: '60vh' 
    }}>
      <CircularProgress size={60} />
    </Box>
  );
  
  if (isError) return (
    <Box sx={{ p: 3 }}>
      <Alert severity="error" sx={{ borderRadius: 2 }}>
        {error?.message || t('documents:fetch_error')}
      </Alert>
    </Box>
  );

  return (
    <Box dir={isRTL ? 'rtl' : 'ltr'} sx={{ maxWidth: 1200, mx: 'auto', p: { xs: 2, md: 3 } }}>
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        mb: 4, 
        flexDirection: isRTL ? 'row-reverse' : 'row' 
      }}>
        <RTLButton 
          component={RouterLink} 
          to="/documents" 
          startIcon={!isRTL && <ArrowBack />} 
          endIcon={isRTL && <ArrowBack />} 
                  {...getRTLProps('select')}
        >
          {t('common:back')}
        </RTLButton>
        <Typography 
          variant="h4" 
          component="h1" 
          sx={{ 
            flexGrow: 1, 
            fontWeight: 500, 
            color: 'primary.main', 
            textAlign: isRTL ? 'right' : 'left' 
          }}
        >
          {t('documents:edit_title')}
        </Typography>
      </Box>

      {isLocked && (
        <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
          {(() => {
            const localizedStatus = latestAssignment?.status 
              ? t(`documents:assignment_status.${latestAssignment.status}`, latestAssignment.status)
              : 'N/A';
            
            return t('documents:edit_locked_message', {
              defaultValue:
                'Editing is disabled while this document is being processed (current assignment status: {{status}}). If changes are required, please contact the assigner.',
              status: localizedStatus,
            });
          })()}
        </Alert>
      )}

      {submitError && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {submitError}
        </Alert>
      )}

      {/* Main Form Section */}
      <Card sx={{ mb: 4, borderRadius: 2, boxShadow: 1 }}>
        <CardContent sx={{ p: { xs: 2, md: 3 } }}>
          <Box component="form" onSubmit={handleSubmit}>
            {/* Basic Information Section */}
            <Typography 
              variant="h6" 
              sx={{ 
                mb: 3,
                fontWeight: 500,
                borderBottom: '1px solid',
                borderColor: 'divider',
                pb: 1 
              }}
            >
              {t('documents:document_info')}
            </Typography>

            <Grid container spacing={3}>
              {/* Title and Reference Number */}
              <Grid item xs={12} md={6}>
                <RTLTextField 
                  fullWidth
                  size="small"
                  variant="outlined"
                  required 
                  label={t('documents:title')}
                  name="title" 
                  value={formData.title}
                  onChange={handleChange} 
                  error={!!formErrors.title} 
                  helperText={formErrors.title}
                  {...getRTLProps('textfield')}
                />
              </Grid>

 {/* Document Type, Priority, Status */}
 <Grid item xs={12} sm={6} md={6}>
                <RTLFormControl 
                  fullWidth 
                  required 
                  error={!!formErrors.type} 
                  size="small"
                  {...getRTLProps('textfield')}
                >
                  <InputLabel>{t('documents:type')}</InputLabel>
                  <Select 
                    name="type" 
                    value={formData.type} 
                    onChange={handleChange} 
                    label={t('documents:type')}
                    MenuProps={{ 
                      sx: { 
                        '& .MuiPaper-root': {
                          direction: isRTL ? 'rtl' : 'ltr'
                        },
                        '& .MuiMenuItem-root': {
                          justifyContent: isRTL ? 'flex-end' : 'flex-start'
                        }
                      } 
                    }}
                  >
                    {documentTypes.map(type => (
                      <MenuItem key={type} value={type}>
                        {type}
                      </MenuItem>
                    ))}
                  </Select>
                  {formErrors.type && <FormHelperText>{formErrors.type}</FormHelperText>}
                </RTLFormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={6}>
                <RTLFormControl 
                  fullWidth 
                  required 
                  error={!!formErrors.priority} 
                  size="small"
                  {...getRTLProps('textfield')}
                >
                  <InputLabel>{t('documents:priority')}</InputLabel>
                  <Select 
                    name="priority" 
                    value={formData.priority} 
                    onChange={handleChange} 
                    label={t('documents:priority')}
                    MenuProps={{ 
                      sx: { 
                        '& .MuiPaper-root': {
                          direction: isRTL ? 'rtl' : 'ltr'
                        },
                        '& .MuiMenuItem-root': {
                          justifyContent: isRTL ? 'flex-end' : 'flex-start'
                        }
                      } 
                    }}
                  >
                    {priorities.map(priority => (
                      <MenuItem key={priority} value={priority}>
                        {priority}
                      </MenuItem>
                    ))}
                  </Select>
                  {formErrors.priority && <FormHelperText>{formErrors.priority}</FormHelperText>}
                </RTLFormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <RTLTextField 
                  fullWidth
                  size="small"
                  variant="outlined"
                  required 
                  label={t('documents:reference_number')}
                  name="reference_number"
                  value={formData.reference_number} 
                  onChange={handleChange} 
                  error={!!formErrors.reference_number}
                  helperText={formErrors.reference_number}
                  {...getRTLProps('textfield')}
                />
              </Grid>

             

              {/* Dates Section */}
              <Grid item xs={12} sm={6} md={6}>
                <LocalizedDatePicker
                  label={t('documents:document_date')}
                  name="document_date"
                  value={formData.document_date}
                  onChange={(e) => handleChange(e)}
                  {...getRTLProps('textfield')}
                  inputProps={{
                    helperText:
                      dateFormat !== DATE_FORMATS.GREGORIAN && formData.document_date
                        ? `${t('documents:displayed_as')}: ${formatDate(formData.document_date)}`
                        : '',
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={6}>
                <RTLFormControl fullWidth size="small" {...getRTLProps('select')}>
                  <InputLabel>{t('documents:doc_source_type')}</InputLabel>
                  <Select 
                    name="source_type" 
                    value={formData.source_type} 
                    onChange={handleChange} 
                    label={t('documents:doc_source_type')}
                    MenuProps={{ 
                      sx: { 
                        '& .MuiPaper-root': {
                          direction: isRTL ? 'rtl' : 'ltr'
                        },
                        '& .MuiMenuItem-root': {
                          justifyContent: isRTL ? 'flex-end' : 'flex-start'
                        }
                      } 
                    }}
                  >
                    {sourceTypes.map(source => (
                      <MenuItem key={source.value} value={source.value}>
                        {source.label}
                      </MenuItem>
                    ))}
                  </Select>
                </RTLFormControl>
              </Grid>

              {formData.source_type !== 'INTERNAL' && (
                <>
                  <Grid item xs={12} sm={6} md={6}>
                    <RTLTextField 
                      fullWidth
                      size="small"
                      variant="outlined"
                      label={t('documents:doc_external_source')}
                      name="external_source_name" 
                      value={formData.external_source_name}
                      onChange={handleChange} 
                      error={!!formErrors.external_source_name}
                      helperText={formErrors.external_source_name}
                      required
                      {...getRTLProps('textfield')}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={6}>
                    <LocalizedDatePicker
                      label={t('documents:archive_registration_date')}
                      value={formData.archive_registration_date}
                      onChange={(e) => handleChange(e)}
                      name="archive_registration_date"
                      {...getRTLProps('textfield')}
                      inputProps={{
                        helperText:
                          dateFormat !== DATE_FORMATS.GREGORIAN && formData.archive_registration_date
                            ? `${t('documents:displayed_as')}: ${formatDate(formData.archive_registration_date)}`
                            : '',
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={6}>
                    <RTLTextField 
                      fullWidth
                      size="small"
                      variant="outlined"
                      label={t('documents:archive_number')}
                      name="archive_number" 
                      value={formData.archive_number}
                      onChange={handleChange}
                      {...getRTLProps('textfield')}
                    />
                  </Grid>
                </>
              )}
              
              <Grid item xs={12} sm={6} md={6}>
                <RTLTextField 
                  fullWidth
                  size="small"
                  variant="outlined"
                  label={t('documents:number_of_pages')}
                  name="number_of_pages" 
                  value={formData.number_of_pages}
                  onChange={handleChange} 
                  type="number"
                  {...getRTLProps('textfield')}
                />
              </Grid>
             

              {/* Description */}
              <Grid item xs={12}>
                <RTLTextField 
                  fullWidth
                  size="small"
                  variant="outlined"
                  label={t('documents:description')}
                  name="description" 
                  value={formData.description}
                  onChange={handleChange} 
                  multiline 
                  rows={3}
                  {...getRTLProps('textfield')}
                />
              </Grid>
           

            
            </Grid>

            <Divider sx={{ my: 4 }} />

            {/* Form Actions */}
            <Box sx={{ 
              display: 'flex', 
              justifyContent: isRTL ? 'flex-start' : 'flex-end', 
              gap: 2 
            }}>
              <RTLButton 
                variant="outlined" 
                component={RouterLink} 
                to="/documents" 
                startIcon={!isRTL && <Cancel />} 
                endIcon={isRTL && <Cancel />} 
                  {...getRTLProps('select')}
              >
                {t('common:cancel')}
              </RTLButton>
              <RTLButton 
                variant="contained" 
                type="submit" 
                disabled={updateLoading || isLocked} 
                startIcon={!isRTL && <Save />} 
                endIcon={isRTL && <Save />} 
                  {...getRTLProps('select')}
              >
                {updateLoading ? <CircularProgress size={24} sx={{ mr: 1 }} /> : null}
                {t('common:save')}
              </RTLButton>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default DocumentEdit;